import "@/index.css";
import { useState, useEffect, lazy, Suspense } from "react";
import { BrowserRouter, Routes, Route, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";
import { AuthProvider } from "@/context/AuthContext";
import { SettingsProvider } from "@/context/SettingsContext";
import { WishlistProvider } from "@/context/WishlistContext";
import { Navbar, Footer } from "@/components/Layout";
import { api } from "@/lib/api";
import Home from "@/pages/Home";
import Events from "@/pages/Events";
import Venues from "@/pages/Venues";
import Artists from "@/pages/Artists";
import EventDetail from "@/pages/EventDetail";
import VenueDetail from "@/pages/VenueDetail";
import ArtistDetail from "@/pages/ArtistDetail";
import Submit from "@/pages/Submit";
import Login from "@/pages/Login";
import Register from "@/pages/Register";
const Dashboard = lazy(() => import("@/pages/Dashboard"));
const Admin = lazy(() => import("@/pages/Admin"));
import AuthCallback from "@/pages/AuthCallback";
import Gigs from "@/pages/Gigs";
import GigDetail from "@/pages/GigDetail";
import SubmitGig from "@/pages/SubmitGig";
import PageView, { RenderedPage } from "@/pages/PageView";
import { PrivacyPolicy, TermsOfUse, CancelMembership, DataDeletion } from "@/pages/Legal";
const PageBuilder = lazy(() => import("@/components/builder/PageBuilder"));
import ChooseRole from "@/pages/ChooseRole";
import Deals from "@/pages/Deals";
import Attractions from "@/pages/Attractions";
import Businesses from "@/pages/Businesses";
import BusinessDetail from "@/pages/BusinessDetail";
import AttractionDetail from "@/pages/AttractionDetail";
import Activities from "@/pages/Activities";
import ActivityDetail from "@/pages/ActivityDetail";
import BarDad, { BarDadDetail } from "@/pages/BarDad";
import SearchResults from "@/pages/SearchResults";
import PlanTrip, { TripView } from "@/pages/PlanTrip";
import DealDetail from "@/pages/DealDetail";
import Hiring from "@/pages/Hiring";
import HiringDetail from "@/pages/HiringDetail";
import SubmitJob from "@/pages/SubmitJob";
import Pricing from "@/pages/Pricing";
import BillingSuccess from "@/pages/BillingSuccess";
import Awards from "@/pages/Awards";
import AwardCategory from "@/pages/AwardCategory";
import SalesPage from "@/pages/SalesPage";
import KnowledgeBase from "@/pages/KnowledgeBase";
import InsiderReview from "@/pages/InsiderReview";
import GiftCards, { GiftCardCheck, GiftCardReloadSuccess, GiftCardPurchaseSuccess } from "@/pages/GiftCards";
import SandyWidget from "@/components/assistant/SandyWidget";
import Marketplace from "@/pages/Marketplace";
import Cart from "@/pages/Cart";
import Checkout from "@/pages/Checkout";
import OrderConfirmation from "@/pages/OrderConfirmation";
import PayInvoice from "@/pages/PayInvoice";
import MyOrders from "@/pages/MyOrders";
import MyRewards from "@/pages/MyRewards";
import VipRewards from "@/pages/VipRewards";
import Wishlist from "@/pages/Wishlist";
import JoinRewards, { ReferralAutoClaim } from "@/pages/JoinRewards";
import ImpersonationBanner from "@/components/ImpersonationBanner";

function HomeRoute() {
  const [page, setPage] = useState(undefined);
  useEffect(() => { api.get("/home-page").then((r) => setPage(r.data)).catch(() => setPage(null)); }, []);
  if (page === undefined) return <div className="py-40 flex justify-center text-zinc-500"><Loader2 className="animate-spin" /></div>;
  if (page) return <RenderedPage page={page} />;
  return <Home />;
}

function Shell() {
  const isAdmin = useLocation().pathname.startsWith("/admin");
  return (
    <>
      {!isAdmin && <Navbar />}
      <Suspense fallback={<div className="min-h-[60vh] flex items-center justify-center"><Loader2 className="animate-spin text-rose-500" size={28} /></div>}>
      <Routes>
        <Route path="/" element={<HomeRoute />} />
        <Route path="/events" element={<Events />} />
        <Route path="/search" element={<SearchResults />} />
        <Route path="/plan-trip" element={<PlanTrip />} />
        <Route path="/trip/:id" element={<TripView />} />
        <Route path="/events/:slug" element={<EventDetail />} />
        <Route path="/venues" element={<Venues />} />
        <Route path="/venues/:slug" element={<VenueDetail />} />
        <Route path="/artists" element={<Artists />} />
        <Route path="/artists/:slug" element={<ArtistDetail />} />
        <Route path="/gigs" element={<Gigs />} />
        <Route path="/gigs/:slug" element={<GigDetail />} />
        <Route path="/deals" element={<Deals />} />
        <Route path="/deals/:slug" element={<DealDetail />} />
        <Route path="/attractions" element={<Attractions />} />
        <Route path="/attractions/:slug" element={<AttractionDetail />} />
        <Route path="/activities" element={<Activities />} />
        <Route path="/activities/:slug" element={<ActivityDetail />} />
        <Route path="/businesses" element={<Businesses />} />
        <Route path="/businesses/:slug" element={<BusinessDetail />} />
        <Route path="/bar-dad" element={<BarDad />} />
        <Route path="/bar-dad/:slug" element={<BarDadDetail />} />
        <Route path="/hiring" element={<Hiring />} />
        <Route path="/hiring/:slug" element={<HiringDetail />} />
        <Route path="/submit-job" element={<SubmitJob />} />
        <Route path="/submit-gig" element={<SubmitGig />} />
        <Route path="/submit" element={<Submit />} />
        <Route path="/insider-review" element={<InsiderReview />} />
        <Route path="/gift-cards" element={<GiftCards />} />
        <Route path="/gift-cards/check" element={<GiftCardCheck />} />
        <Route path="/gift-cards/reload-success" element={<GiftCardReloadSuccess />} />
        <Route path="/gift-cards/purchase-success" element={<GiftCardPurchaseSuccess />} />
        <Route path="/pricing" element={<Pricing />} />
        <Route path="/pricing/:target" element={<Pricing />} />
        <Route path="/billing/success" element={<BillingSuccess />} />
        <Route path="/awards" element={<Awards />} />
        <Route path="/awards/:season" element={<Awards />} />
        <Route path="/awards/:season/:category" element={<AwardCategory />} />
        <Route path="/for-venues" element={<SalesPage slug="for-venues" />} />
        <Route path="/for-artists" element={<SalesPage slug="for-artists" />} />
        <Route path="/for-attractions" element={<SalesPage slug="for-attractions" />} />
        <Route path="/for-activities" element={<SalesPage slug="for-activities" />} />
        <Route path="/for-users" element={<SalesPage slug="for-users" />} />
        <Route path="/venue-profile" element={<SalesPage slug="venue-profile" />} />
        <Route path="/venue-dashboard" element={<SalesPage slug="venue-dashboard" />} />
        <Route path="/venue-marketplace" element={<SalesPage slug="venue-marketplace" />} />
        <Route path="/venue-events" element={<SalesPage slug="venue-events" />} />
        <Route path="/venue-loyalty" element={<SalesPage slug="venue-loyalty" />} />
        <Route path="/venue-hiring" element={<SalesPage slug="venue-hiring" />} />
        <Route path="/artist-profile" element={<SalesPage slug="artist-profile" />} />
        <Route path="/artist-dashboard" element={<SalesPage slug="artist-dashboard" />} />
        <Route path="/artist-marketplace" element={<SalesPage slug="artist-marketplace" />} />
        <Route path="/artist-events" element={<SalesPage slug="artist-events" />} />
        <Route path="/artist-loyalty" element={<SalesPage slug="artist-loyalty" />} />
        <Route path="/artist-gigs" element={<SalesPage slug="artist-gigs" />} />
        <Route path="/attraction-profile" element={<SalesPage slug="attraction-profile" />} />
        <Route path="/attraction-dashboard" element={<SalesPage slug="attraction-dashboard" />} />
        <Route path="/attraction-marketplace" element={<SalesPage slug="attraction-marketplace" />} />
        <Route path="/attraction-events" element={<SalesPage slug="attraction-events" />} />
        <Route path="/attraction-loyalty" element={<SalesPage slug="attraction-loyalty" />} />
        <Route path="/attraction-hiring" element={<SalesPage slug="attraction-hiring" />} />
        <Route path="/activity-profile" element={<SalesPage slug="activity-profile" />} />
        <Route path="/activity-dashboard" element={<SalesPage slug="activity-dashboard" />} />
        <Route path="/activity-marketplace" element={<SalesPage slug="activity-marketplace" />} />
        <Route path="/activity-events" element={<SalesPage slug="activity-events" />} />
        <Route path="/activity-loyalty" element={<SalesPage slug="activity-loyalty" />} />
        <Route path="/activity-hiring" element={<SalesPage slug="activity-hiring" />} />
        <Route path="/user-rewards" element={<SalesPage slug="user-rewards" />} />
        <Route path="/user-wishlist" element={<SalesPage slug="user-wishlist" />} />
        <Route path="/user-tickets" element={<SalesPage slug="user-tickets" />} />
        <Route path="/user-trips" element={<SalesPage slug="user-trips" />} />
        <Route path="/knowledge-base" element={<KnowledgeBase />} />
        <Route path="/knowledge-base/:slug" element={<KnowledgeBase />} />
        <Route path="/marketplace" element={<Marketplace />} />
        <Route path="/marketplace/:slug" element={<Marketplace />} />
        <Route path="/cart" element={<Cart />} />
        <Route path="/checkout" element={<Checkout />} />
        <Route path="/order-confirmation" element={<OrderConfirmation />} />
        <Route path="/pay/:orderId" element={<PayInvoice />} />
        <Route path="/my-orders" element={<MyOrders />} />
        <Route path="/my-rewards" element={<MyRewards />} />
        <Route path="/vip-rewards" element={<VipRewards />} />
        <Route path="/wishlist" element={<Wishlist />} />
        <Route path="/join-rewards" element={<JoinRewards />} />
        <Route path="/p/:slug" element={<PageView />} />
        <Route path="/privacy-policy" element={<PrivacyPolicy />} />
        <Route path="/terms-of-use" element={<TermsOfUse />} />
        <Route path="/cancel-membership" element={<CancelMembership />} />
        <Route path="/data-deletion" element={<DataDeletion />} />
        <Route path="/login" element={<Login />} />
        <Route path="/register" element={<Register />} />
        <Route path="/dashboard" element={<Dashboard />} />
        <Route path="/choose-role" element={<ChooseRole />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="/admin/builder/:pageId" element={<PageBuilder />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
      </Routes>
      </Suspense>
      {!isAdmin && <Footer />}
      <SandyWidget />
      <ReferralAutoClaim />
      <ImpersonationBanner />
    </>
  );
}

function App() {
  return (
    <AuthProvider>
      <SettingsProvider>
        <WishlistProvider>
          <BrowserRouter>
            <Toaster position="top-center" theme="dark" richColors />
            <Shell />
          </BrowserRouter>
        </WishlistProvider>
      </SettingsProvider>
    </AuthProvider>
  );
}

export default App;
